import type { Icon } from '@tabler/icons-react';
import { Link, useLocation } from 'react-router-dom';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { userHasType } from '@/lib/authz';
import { cn } from '@/lib/utils';

export function NavMain({
  items,
}: {
  items: {
    title: string;
    url: string;
    icon?: Icon;
    allowedTypes?: string[] | undefined;
    counter?: number | undefined;
  }[];
}) {
  const location = useLocation();
  const activePath = location.pathname;
  const { isMobile, setOpenMobile } = useSidebar();
  const { user } = useKeycloakAuth();

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {items
            .filter((item) => userHasType(user, item.allowedTypes))
            .map((item) => {
              const isActive =
                activePath === item.url || (activePath === '/' && item.url === '/dashboard');
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    tooltip={item.title}
                    isActive={isActive}
                    className="text-base md:text-sm h-12 md:h-8"
                    asChild
                  >
                    <Link
                      to={item.url}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                      }}
                      className="flex items-center justify-between w-full"
                    >
                      <div className="flex items-center gap-2">
                        {item.icon && <item.icon className="size-4 shrink-0" />}
                        <span className="text-base md:text-sm">{item.title}</span>
                      </div>
                      {item.counter && (
                        <span
                          className={cn(
                            'inline-flex ml-auto h-5 w-8 items-center justify-center rounded-md text-xs font-semibold',
                            isActive
                              ? 'border border-neutral-900/15 bg-neutral-900/10 text-neutral-900 dark:border-white/15 dark:bg-white/10 dark:text-neutral-100'
                              : 'bg-neutral-200 text-neutral-800'
                          )}
                        >
                          {item.counter}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
